import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Animated, StyleProp, ViewStyle } from 'react-native';

export type TrafficLightStatus = 'red' | 'amber' | 'green' | 'warning';

interface TrafficLightIndicatorProps {
    status: TrafficLightStatus;
    current?: number;
    target?: number;
    itemLabel?: string;
    label?: string;
    style?: StyleProp<ViewStyle>;
    testID?: string;
}

export const TrafficLightIndicator: React.FC<TrafficLightIndicatorProps> = ({
    status,
    current = 0,
    target = 0,
    itemLabel = 'items',
    label,
    style,
    testID = 'traffic-light-indicator',
}) => {
    const pulseAnim = useRef(new Animated.Value(1)).current;

    useEffect(() => {
        if (status === 'amber' || status === 'green') {
            const animation = Animated.loop(
                Animated.sequence([
                    Animated.timing(pulseAnim, { toValue: 1.15, duration: 400, useNativeDriver: true }),
                    Animated.timing(pulseAnim, { toValue: 1, duration: 400, useNativeDriver: true }),
                ])
            );
            animation.start();
            return () => animation.stop();
        } else {
            pulseAnim.setValue(1);
        }
    }, [status, pulseAnim]);

    const getStatusMessage = () => {
        if (label) return label;
        switch (status) {
            case 'red':
                return `Start moving ${itemLabel}! (0 / ${target})`;
            case 'amber':
                return `${current} of ${target} — keep going!`;
            case 'green':
                return `⭐ Perfect! ${target} of ${target} placed!`;
            case 'warning':
                return `⚠️ Too many! Tap an item to return it (${current} / ${target})`;
        }
    };

    const isRedActive = status === 'red';
    const isAmberActive = status === 'amber' || status === 'warning';
    const isGreenActive = status === 'green';

    return (
        <View testID={testID} style={[styles.container, style]}>
            {/* 3D Traffic Light Capsule */}
            <View style={styles.housing}>
                {/* Red Lamp */}
                <View
                    style={[
                        styles.lamp,
                        styles.redLampOff,
                        isRedActive && styles.redLampOn,
                    ]}
                >
                    {isRedActive && <View style={styles.lampGlow} />}
                </View>

                {/* Amber Lamp */}
                <Animated.View
                    style={[
                        styles.lamp,
                        styles.amberLampOff,
                        isAmberActive && styles.amberLampOn,
                        isAmberActive && { transform: [{ scale: pulseAnim }] },
                    ]}
                >
                    {isAmberActive && <View style={styles.lampGlow} />}
                </Animated.View>

                {/* Green Lamp */}
                <Animated.View
                    style={[
                        styles.lamp,
                        styles.greenLampOff,
                        isGreenActive && styles.greenLampOn,
                        isGreenActive && { transform: [{ scale: pulseAnim }] },
                    ]}
                >
                    {isGreenActive && <View style={styles.lampGlow} />}
                </Animated.View>
            </View>

            {/* Status Feedback Capsule */}
            <View
                style={[
                    styles.messageBadge,
                    status === 'red' && styles.messageBadgeRed,
                    status === 'amber' && styles.messageBadgeAmber,
                    status === 'green' && styles.messageBadgeGreen,
                    status === 'warning' && styles.messageBadgeWarning,
                ]}
            >
                <Text
                    style={[
                        styles.messageText,
                        status === 'red' && styles.messageTextRed,
                        status === 'amber' && styles.messageTextAmber,
                        status === 'green' && styles.messageTextGreen,
                        status === 'warning' && styles.messageTextWarning,
                    ]}
                    allowFontScaling={true}
                    maxFontSizeMultiplier={1.4}
                >
                    {getStatusMessage()}
                </Text>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        paddingVertical: 8,
    },
    housing: {
        flexDirection: 'row',
        backgroundColor: '#1E293B',
        paddingHorizontal: 8,
        paddingVertical: 6,
        borderRadius: 24,
        gap: 8,
        borderWidth: 2,
        borderColor: '#334155',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.2,
        shadowRadius: 4,
        elevation: 3,
    },
    lamp: {
        width: 22,
        height: 22,
        borderRadius: 11,
        alignItems: 'center',
        justifyContent: 'center',
    },
    lampGlow: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: 'rgba(255, 255, 255, 0.65)',
        position: 'absolute',
        top: 2,
        left: 4,
    },
    redLampOff: {
        backgroundColor: '#450A0A',
        borderColor: '#7F1D1D',
        borderWidth: 1,
    },
    redLampOn: {
        backgroundColor: '#EF4444',
        shadowColor: '#EF4444',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.9,
        shadowRadius: 8,
        elevation: 6,
    },
    amberLampOff: {
        backgroundColor: '#451A03',
        borderColor: '#78350F',
        borderWidth: 1,
    },
    amberLampOn: {
        backgroundColor: '#F59E0B',
        shadowColor: '#F59E0B',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.9,
        shadowRadius: 8,
        elevation: 6,
    },
    greenLampOff: {
        backgroundColor: '#052E16',
        borderColor: '#14532D',
        borderWidth: 1,
    },
    greenLampOn: {
        backgroundColor: '#22C55E',
        shadowColor: '#22C55E',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.9,
        shadowRadius: 10,
        elevation: 8,
    },
    messageBadge: {
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: 16,
        borderWidth: 1.5,
    },
    messageBadgeRed: {
        backgroundColor: '#FEF2F2',
        borderColor: '#FECACA',
    },
    messageBadgeAmber: {
        backgroundColor: '#FFFBEB',
        borderColor: '#FDE68A',
    },
    messageBadgeGreen: {
        backgroundColor: '#F0FDF4',
        borderColor: '#86EFAC',
    },
    messageBadgeWarning: {
        backgroundColor: '#FFF7ED',
        borderColor: '#FDBA74',
    },
    messageText: {
        fontSize: 16,
        fontWeight: '800',
    },
    messageTextRed: {
        color: '#DC2626',
    },
    messageTextAmber: {
        color: '#D97706',
    },
    messageTextGreen: {
        color: '#15803D',
    },
    messageTextWarning: {
        color: '#C2410C',
    },
});
